// Identification des fournisseurs avant la génération d'un ERA (Analyse CSPE, étape 3).
//
// L'identification ne fait que renseigner le fournisseur de chaque facture : l'ERA se
// calcule sur les lignes CSPE déjà enregistrées (OCR, corrections, saisie manuelle).
// Une facture d'un fournisseur inconnu ne doit donc pas bloquer la génération — cas
// SÉLIA du 29/10/2026 : lignes saisies à la main, ERA impossible à cause d'une 500 sur
// l'identification. On identifie tout, on recense les échecs, et l'appelant prévient.

/** Message lisible d'un échec axios (message du backend s'il y en a un). */
export function messageErreur(raison) {
  return raison?.response?.data?.message || raison?.message || 'erreur inconnue';
}

/** Échec « fournisseur non reconnu » (422 FOURNISSEUR_INCONNU) : seul échec toléré. */
export function estFournisseurInconnu(raison) {
  return raison?.response?.status === 422 && raison?.response?.data?.code === 'FOURNISSEUR_INCONNU';
}

/**
 * Identifie le fournisseur de chaque facture. Seul un fournisseur NON RECONNU est
 * toléré (recensé, l'appelant prévient) ; toute autre erreur (panne S3, base, réseau,
 * session) est relancée : ce n'est pas un fournisseur inconnu et l'ERA ne doit pas
 * partir sur un diagnostic faux.
 *
 * @param {Array<{original_file_name?: string}>} documents factures à identifier
 * @param {(doc) => Promise<unknown>} identifier appel réseau pour UNE facture
 * @returns {Promise<Array<{ doc, message: string }>>} factures au fournisseur non reconnu
 */
export async function identifierFournisseursSansBloquer(documents, identifier) {
  const resultats = await Promise.allSettled((documents || []).map((doc) => identifier(doc)));
  const echecs = resultats
    .map((r, i) => ({ r, doc: documents[i] }))
    .filter(({ r }) => r.status === 'rejected');
  const autre = echecs.find(({ r }) => !estFournisseurInconnu(r.reason));
  if (autre) throw autre.r.reason;
  return echecs.map(({ r, doc }) => ({ doc, message: messageErreur(r.reason) }));
}

/**
 * Texte de l'avertissement affiché quand des factures n'ont pas été identifiées.
 * @param {{ taxe?: string, cerfa?: string }} [libelles] « CSPE » / « l'ERA » par défaut ;
 *   « TICGN » / « l'ERA-G » pour le gaz.
 */
export function avertissementNonIdentifiees(nonIdentifiees, { taxe = 'CSPE', cerfa = "l'ERA" } = {}) {
  if (!nonIdentifiees || nonIdentifiees.length === 0) return null;
  return `${nonIdentifiees.length} facture(s) sans fournisseur reconnu : ${cerfa} est calculé `
    + `avec les lignes ${taxe} déjà enregistrées (saisie manuelle comprise). ${nonIdentifiees[0].message}`;
}
